import React, { useState, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import toast from 'react-hot-toast';
import { toastPermissionDenied } from '../utils/permissionToast';
import { useAuth } from '../contexts/AuthContext';
import AppraisalForm from '../components/AppraisalForm';
import { canWriteCompensationFor } from '../utils/compensationPermissions';

const AddAppraisal = () => {
  const { hasPermission, user } = useAuth();
  const methods = useForm();
  const { handleSubmit, formState: { isSubmitting } } = methods;
  const navigate = useNavigate();
  
  const [employees, setEmployees] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);

  useEffect(() => {
    if (!hasPermission('appraisals', 'create')) {
      toastPermissionDenied('You do not have permission to add appraisals');
      navigate('/appraisals');
      return;
    }
    // Fetch all employees for dropdown (use high limit to get all)
    api.get('/api/employees', { params: { limit: 10000 } })
      .then(res => setEmployees(res.data.data || []))
      .catch(err => {
        console.error('Error fetching employees:', err);
        toast.error('Failed to load employees');
      });
    // Payment method is required, so without this list the form cannot be submitted.
    api.get('/api/payment-methods')
      .then(res => setPaymentMethods(res.data.data || []))
      .catch(err => {
        console.error('Error fetching payment methods:', err);
        toast.error('Failed to load payment methods');
      });
  }, [hasPermission, navigate]);

  // The server keeps a caller off their own appraisal unless they hold update_all, so a
  // department manager is not offered themselves.
  const pickableEmployees = employees.filter(
    (emp) => canWriteCompensationFor(hasPermission, 'appraisals', user, emp.id)
  );

  const onSubmit = async (data) => {
    try {
      await api.post('/api/appraisals', data);
      toast.success('Appraisal added successfully!');
      navigate('/appraisals');
    } catch (error) {
      // express-validator 400s carry the reason in errors[], not message.
      toast.error(
        error.response?.data?.message
          || error.response?.data?.errors?.[0]?.msg
          || 'Failed to add appraisal'
      );
    }
  };

  return (
    <div className="max-w-2xl mx-auto bg-white p-8 rounded-lg shadow-soft">
      <h2 className="text-2xl font-bold mb-6">Add Appraisal</h2>
      <FormProvider {...methods}>
        {/* noValidate: react-hook-form owns validation. Without it the raise amount's
            step="0.01" puts up the browser's own popup instead of the field message. */}
        <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
          <AppraisalForm 
            employees={pickableEmployees}
            paymentMethods={paymentMethods}
            mode="add"
          />
          <button type="submit" disabled={isSubmitting} data-testid="appraisals-submit" className="w-full bg-primary-600 text-white py-2 rounded-md hover:bg-primary-700 transition-colors">
            {isSubmitting ? 'Adding...' : 'Add Appraisal'}
          </button>
        </form>
      </FormProvider>
    </div>
  );
};

export default AddAppraisal; 